/**
 * PlaylistTrash — v3.0.152
 *
 * User-facing bin for soft-deleted playlists. Same 30-day window as
 * song trash. Restoring preserves the playlist_id + access_code, so
 * every existing share URL keeps working.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Trash2, RotateCcw, ListMusic, Loader2, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiFetch } from '../App';

const fmtRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const fmtRemaining = (iso) => {
  if (!iso) return '';
  const purgeAt = new Date(iso).getTime() + 30 * 24 * 60 * 60 * 1000;
  const remaining = purgeAt - Date.now();
  if (remaining <= 0) return 'purging soon';
  const days = Math.floor(remaining / 86400000);
  return days > 0 ? `auto-purges in ${days}d` : `auto-purges in ${Math.floor(remaining / 3600000)}h`;
};

export default function PlaylistTrash() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/playlists/trash');
      setPlaylists(Array.isArray(data?.playlists) ? data.playlists : []);
    } catch (err) { toast.error(err.message || 'Failed to load trash'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleRestore = async (pid) => {
    setBusyId(pid);
    try {
      await apiFetch(`/playlists/${pid}/restore`, { method: 'POST' });
      setPlaylists((prev) => prev.filter((p) => p.playlist_id !== pid));
      toast.success('Playlist restored — share link + code unchanged');
    } catch (err) { toast.error(err.message || 'Restore failed'); }
    finally { setBusyId(null); }
  };

  const handleHardDelete = async (p) => {
    if (!window.confirm(
      `Permanently delete "${p.name || 'this playlist'}"?\n\n`
      + `The playlist and every comment on it will be gone forever. Songs are NOT affected.`,
    )) return;
    setBusyId(p.playlist_id);
    try {
      await apiFetch(`/playlists/${p.playlist_id}?permanent=true`, { method: 'DELETE' });
      setPlaylists((prev) => prev.filter((x) => x.playlist_id !== p.playlist_id));
      toast.success('Playlist deleted forever');
    } catch (err) { toast.error(err.message || 'Delete failed'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 to-black text-white pb-32">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link to="/playlists" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-6">
          <ArrowLeft size={14} /> Back to Playlists
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-rose-500/10 rounded-lg">
            <Trash2 className="text-rose-300" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" data-testid="playlist-trash-page-title">Recently deleted playlists</h1>
            <p className="text-sm text-zinc-400">Playlists stay here for 30 days. Restoring keeps every share link intact.</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-zinc-500">
            <Loader2 size={24} className="animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : playlists.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-lg">
            <Trash2 size={40} className="text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">Nothing here. Deleted playlists will show up in this bin.</p>
          </div>
        ) : (
          <div className="border border-white/10 rounded-lg overflow-hidden">
            {playlists.map((p) => (
              <div
                key={p.playlist_id}
                className="flex items-center gap-3 px-3 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/5"
                data-testid={`trash-playlist-${p.playlist_id}`}
              >
                {p.cover_url ? (
                  <img src={p.cover_url} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded bg-gradient-to-br from-emerald-500/30 to-purple-500/30 flex items-center justify-center shrink-0">
                    <ListMusic size={18} className="text-white/60" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{p.name || 'Untitled playlist'}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
                    <span>{p.song_count} song{p.song_count === 1 ? '' : 's'}</span>
                    <span className="text-zinc-600">·</span>
                    <span>Deleted {fmtRelative(p.deleted_at)}</span>
                    <span className="text-zinc-600">·</span>
                    <span className="text-amber-400/80 flex items-center gap-1">
                      <AlertTriangle size={10} /> {fmtRemaining(p.deleted_at)}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => handleRestore(p.playlist_id)}
                  disabled={busyId === p.playlist_id}
                  size="sm"
                  data-testid={`trash-playlist-restore-${p.playlist_id}`}
                  className="bg-emerald-500 text-black hover:bg-emerald-400"
                >
                  {busyId === p.playlist_id ? <Loader2 size={12} className="animate-spin mr-1" /> : <RotateCcw size={12} className="mr-1" />}
                  Restore
                </Button>
                <Button
                  onClick={() => handleHardDelete(p)}
                  disabled={busyId === p.playlist_id}
                  variant="outline"
                  size="sm"
                  data-testid={`trash-playlist-purge-${p.playlist_id}`}
                  className="border-rose-500/40 text-rose-300 hover:bg-rose-500/20"
                >
                  <Trash2 size={12} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
